import adminApi from '@/apis/admin'

// SDD U2.2 — admin prompt editor store module.
// Folder name `adminPrompts` becomes the module name (see frontend/CLAUDE.md auto-loader).
// Mutations/actions share a single global namespace, so all identifiers below are prefixed
// with `PROMPTS` / `Prompt` to stay unique across sibling modules (adminScenarios uses
// SET_ADMIN_SCENARIOS*, adminAudit uses SET_ADMIN_AUDIT*, etc).

const state = {
  list: [], // [{ language, prompt_doc_id, display_name, voice, version, updated_at, has_prompt_doc, has_flow, flow_enabled }]
  current: null, // single prompt_doc being edited — { language, system_instruction, initial_prompt, variables, pronunciation_map, version, ... }
  loading: false,
  saving: false,
  error: null
}

const mutations = {
  SET_PROMPTS_LIST(state, payload) {
    state.list = payload || []
  },
  SET_PROMPT_CURRENT(state, payload) {
    state.current = payload
  },
  SET_PROMPTS_LOADING(state, v) {
    state.loading = !!v
  },
  SET_PROMPTS_SAVING(state, v) {
    state.saving = !!v
  },
  SET_PROMPTS_ERROR(state, e) {
    state.error = e || null
  }
}

const actions = {
  async loadPromptsList({ commit }, scenario_id) {
    commit('SET_PROMPTS_LOADING', true)
    commit('SET_PROMPTS_ERROR', null)
    // Codex iter 3 (F8): clear `list` BEFORE the request so rows from
    // scenario A never display under scenario B's header (or wire
    // scenario B's id to scenario A's stale `item.language` on Delete).
    commit('SET_PROMPTS_LIST', [])
    try {
      const { data } = await adminApi.listPrompts(scenario_id)
      if (!data.success) throw new Error(data.message || 'Failed to load prompts')
      commit('SET_PROMPTS_LIST', (data.data && data.data.languages) || data.data || [])
    } catch (e) {
      commit('SET_PROMPTS_ERROR', e.message || String(e))
    } finally {
      commit('SET_PROMPTS_LOADING', false)
    }
  },

  async loadPrompt({ commit }, { scenario_id, language }) {
    commit('SET_PROMPTS_LOADING', true)
    commit('SET_PROMPTS_ERROR', null)
    // Codex iter 2 (F7): clear `current` BEFORE issuing the GET so an
    // editor opened on prompt B never renders stale prompt-A content
    // while the request is in flight or after it fails.
    commit('SET_PROMPT_CURRENT', null)
    try {
      const { data } = await adminApi.getPrompt(scenario_id, language)
      if (!data.success) throw new Error(data.message || 'Failed to load prompt')
      commit('SET_PROMPT_CURRENT', data.data)
    } catch (e) {
      commit('SET_PROMPTS_ERROR', e.message || String(e))
    } finally {
      commit('SET_PROMPTS_LOADING', false)
    }
  },

  async createPrompt({ commit }, { scenario_id, language, body }) {
    commit('SET_PROMPTS_SAVING', true)
    commit('SET_PROMPTS_ERROR', null)
    try {
      const { data } = await adminApi.createPrompt(scenario_id, language, body)
      if (!data.success) throw new Error(data.message || 'Save failed')
      return data.data
    } catch (e) {
      commit('SET_PROMPTS_ERROR', e.message || String(e))
      throw e
    } finally {
      commit('SET_PROMPTS_SAVING', false)
    }
  },

  async updatePrompt({ commit }, { scenario_id, language, body }) {
    commit('SET_PROMPTS_SAVING', true)
    commit('SET_PROMPTS_ERROR', null)
    try {
      const { data } = await adminApi.updatePrompt(scenario_id, language, body)
      if (!data.success) {
        // SET_SNACKBAR is a mutation in @/store/snackbar — commit (not dispatch) with { root: true }.
        commit('SET_SNACKBAR', { status: true, color: 'error', text: data.message || 'Save failed', timeout: 4000 }, { root: true })
        commit('SET_PROMPTS_ERROR', data.message || 'Save failed')
        // Re-throw with extra envelope so callers can decide (e.g. show a reload-prompt dialog on 409).
        const err = new Error(data.message || 'Save failed')
        err.response_data = data.data || {}
        err.status_code = data.status_code
        throw err
      }
      return data.data
    } catch (e) {
      if (!e.response_data) commit('SET_PROMPTS_ERROR', e.message || String(e))
      throw e
    } finally {
      commit('SET_PROMPTS_SAVING', false)
    }
  },

  async deletePrompt({ commit }, { scenario_id, language }) {
    commit('SET_PROMPTS_SAVING', true)
    commit('SET_PROMPTS_ERROR', null)
    try {
      const { data } = await adminApi.deletePrompt(scenario_id, language)
      if (!data.success) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: data.message || 'Delete failed', timeout: 4000 }, { root: true })
        commit('SET_PROMPTS_ERROR', data.message || 'Delete failed')
        throw new Error(data.message || 'Delete failed')
      }
      return data.data
    } catch (e) {
      commit('SET_PROMPTS_ERROR', e.message || String(e))
      throw e
    } finally {
      commit('SET_PROMPTS_SAVING', false)
    }
  }
}

export default { state, mutations, actions }
